import { useEffect, useRef } from "react";
import type { OptionSection, RecipientOption } from "@/v5/components/RecipientPicker";
import { cn } from "@/v5/lib/utils";

interface MentionSuggestionsProps {
  open: boolean;
  sections: OptionSection[];
  options: RecipientOption[];
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (option: RecipientOption) => void;
  /** Id the textarea points at with aria-activedescendant. */
  idPrefix: string;
  className?: string;
}

export function optionDomId(idPrefix: string, index: number): string {
  return `${idPrefix}-option-${index}`;
}

/**
 * The name list for inline "@" tagging.
 *
 * Anchored to the textarea rather than the caret: following a caret inside a
 * textarea needs a mirrored copy of the element to measure against, which is a
 * lot of machinery for a menu that reads fine pinned to the bottom edge.
 */
export function MentionSuggestions({
  open,
  sections,
  options,
  activeIndex,
  onHover,
  onSelect,
  idPrefix,
  className,
}: MentionSuggestionsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the keyboard-highlighted row in view as it moves past the fold.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  if (!open || options.length === 0) return null;

  let flatIndex = -1;

  return (
    <div
      ref={listRef}
      id={idPrefix}
      role="listbox"
      aria-label="People and groups to tag"
      className={cn(
        "absolute bottom-full left-0 right-0 z-50 mb-1 max-h-56 overflow-y-auto",
        "rounded-md border border-border bg-popover shadow-md",
        className,
      )}
    >
      {sections.map((section) => (
        <div key={section.id}>
          <div className="sticky top-0 border-b border-border bg-muted/80 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur">
            {section.heading}
          </div>
          {section.items.map((opt) => {
            flatIndex += 1;
            const index = flatIndex;
            const active = index === activeIndex;
            return (
              <button
                key={opt.token}
                id={optionDomId(idPrefix, index)}
                type="button"
                role="option"
                aria-selected={active}
                data-active={active}
                // The textarea must keep focus, or the caret position the
                // insert splices against is gone by the time we read it.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => onHover(index)}
                onClick={() => onSelect(opt)}
                className={cn(
                  "flex w-full items-start gap-2.5 border-b border-border/40 px-3 py-2 text-left text-sm transition-colors last:border-b-0",
                  active ? "bg-accent" : "hover:bg-accent/60",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-foreground">{opt.label}</div>
                  {opt.title && (
                    <div className="truncate text-xs text-muted-foreground">{opt.title}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
